import React, { useState, useEffect, useRef } from "react";
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Box,
  Stack,
  Checkbox,
} from "@chakra-ui/react";
// Main components
import MainLayout from "../../../components/main/layout/layout";
// Common components
import CommonCheckbox from "../../../components/common/checkbox/checkbox";
// Maintenance components
import MaintenanceForm from "../../../components/maintenance/form/form";
// Components
import Upload from "../../../components/upload/upload";
// Actions
import * as mainActions from "../../../redux/actions/main";
import * as liveActions from "../../../redux/actions/live";
import * as filesActions from "../../../redux/actions/files";
// Utils
import { formatDaysOfWeek } from "../../../utils/formatters";
// Styles
import "./form.css";

const LiveForm = ({ isNew }) => {
  const dispatch = useDispatch();
  const childRef = useRef();
  // States
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    "enabled": true,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isDirtyFiles, setIsDirtyFiles] = useState(false);
  const [files_1, setFiles_1] = useState([]);
  const [files_2, setFiles_2] = useState([]);
  // Selectors
  const currentPage = useSelector((state) => state.mainReducer.currentPage);

  // Set constants
  const page = "live";
  const primaryName = !isNew && !!form[`title`] ? form[`title`] : ``;
  const itemNameSingular = "Directe";

  // Get id from url
  let id = null;
  const { id: paramId } = useParams();
  if (!isNew) {
    id = parseInt(paramId);
  }

  useEffect(() => {
    if (!!currentPage) return;

    // Set active page
    dispatch(mainActions.setCurrentPage(page));

    // Set initial breadscrum
    if (isNew) {
      const pathKey = 'new';
      const path = `${page}/${pathKey}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey: 'new-m', itemNameSingular, }]));
    } else {
      const pathKey = 'edit';
      const path = `${page}/${pathKey}/${id}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey, primaryValue: id, primaryName, itemNameSingular, }]));
    }
  }, []);

  const handleChange = (evt) => {
    if (childRef.current) {
      childRef.current.handleChange(evt);
    }
  };

  const handleOnChangeDaysOfWeek = (dayCode, checked) => {
    const fieldName = "days_of_week";
    const daysOrder = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    // obtener días actualmente seleccionados (sin entradas vacías)
    const current = !!form[fieldName]
      ? form[fieldName].split("|").filter(Boolean)
      : [];

    // usar Set para añadir/quitar fácilmente
    const selected = new Set(current);

    if (checked) {
      selected.add(dayCode);
    } else {
      selected.delete(dayCode);
    }

    // crear array ordenado según daysOrder
    const ordered = daysOrder.filter((d) => selected.has(d));

    const daysOfWeekValue = ordered.length ? "|" + ordered.join("|") + "|" : "";
    setForm({
      ...form,
      [fieldName]: daysOfWeekValue,
    });
    setIsDirty(true);
  };

  const renderForm = () => {
    return (
      <div className="live-form-wrapper">
        <div className="live-form-firstcolumn-wrapper">
          {renderMainCard()}
        </div>
        <div className="maintenance-form-card">
          {renderUploadCard_1()}
          {renderUploadCard_2()}
        </div>
      </div>
    );
  };

  const renderMainCard = () => {
    let title;
    if (isNew) {
      title = "Nou Directe";
    } else {
      title = `Directe${!!form[`id`] ? ` amb Id: ${form[`id`]}` : ''}`;
    }

    // Set days of week names
    const daysOfWeekNames = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ]

    return (
      <div className="live-form-maincard">
        <div className="title maintenance-form-title">
          {title}
        </div>  
        {/* IMPORTANT !!! Els checkboxes no poden estar en el mateix FormControl En producció no funciona!! */}
        <CommonCheckbox
          isChecked={!!form['enabled']}
          onChange={(e) => {
            setForm({
              ...form,
              "enabled": e.target.checked,
            });     
            setIsDirty(true);         
          }}
        >
          Habilitat
        </CommonCheckbox>          
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Títol
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Títol"
            name="title"
            data-type={`string`}
            onChange={handleChange}
            value={form[`title`] ? form[`title`] : ``}
          />
        </FormControl>           
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Sub-títol
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Sub-títol"
            name="subtitle"
            data-type={`string`}
            onChange={handleChange}
            value={form[`subtitle`] ? form[`subtitle`] : ``}
          />
        </FormControl>  
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Descripció
          </FormLabel>
          <Textarea
            className="maintenance-form-field"
            placeholder="Descripció"
            name="description"
            data-type={`string`}
            onChange={handleChange}
            value={form[`description`] ? form[`description`] : ``}
          />
        </FormControl>
        <Box 
          mt={4}
          display="flex" 
          flexDirection="row" 
          gap={4}
        >
          <FormControl isRequired w="120px" >
            <FormLabel fontSize="sm" className="maintenance-form-label">
              Hora inici
            </FormLabel>
            <Input
              className="maintenance-form-field"z
              placeholder="Hora"
              name="start_time"
              data-type={`string`}
              onChange={handleChange}
              value={form[`start_time`] ? form[`start_time`] : ``}
              w="120px"
              textAlign="center"
              maxLength={5}
            />
          </FormControl>
          <FormControl isRequired w="120px" >
            <FormLabel fontSize="sm" className="maintenance-form-label">
              Hora final
            </FormLabel>
            <Input
              className="maintenance-form-field"z
              placeholder="Hora"
              name="end_time"
              data-type={`string`}
              onChange={handleChange}
              value={form[`end_time`] ? form[`end_time`] : ``}
              w="120px"
              textAlign="center"
              maxLength={5}
            />
          </FormControl>                  
        </Box>
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Dies disponibles
          </FormLabel>
        </FormControl>
        {/* IMPORTANT !!! Els checkboxes no poden estar en el mateix FormControl En producció no funciona!! */}
        <Stack 
          spacing={1} 
          className="maintenance-form-checkboxlist"
          width="auto"
        >
          {Object.values(daysOfWeekNames).map((dayCode) => (
            <Checkbox
              key={`live-form-checkbox-days_of_week-${dayCode}` }
              isChecked={form['days_of_week'] && form['days_of_week'].includes(dayCode)}
              onChange={(e) => {
                handleOnChangeDaysOfWeek(dayCode, e.target.checked);
              }}
              >
              {formatDaysOfWeek(dayCode)}
            </Checkbox>   
          ))}
        </Stack>           
      </div>   
    );
  };

  const renderUploadCard_1 = () => {
    return (
      <div className="live-form-upload-card">
        <FormControl>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Imatge principal / Portada
          </FormLabel>    
          {/* <Input
            className="maintenance-form-field"
            placeholder="https://"
            name="url_image_1"
            data-type={`string`}
            type="url"
            onChange={handleChange}
            value={form[`url_image_1`] ? form[`url_image_1`] : ``}
          /> */}
        </FormControl>             
        <Upload 
          type={"live"}
          id={id}
          id2={"1"}
          style={{ marginTop: "10px"}}
          onChange={(files) => {
            setFiles_1(files);        
          }}      
          onNewFile={(newFiles) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onDeleteFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onUpdateFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }} 
        />
      </div>
    );
  };

  const renderUploadCard_2 = () => {
    return (
      <div className="live-form-upload-card">
        <FormControl>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Imatge secundaria
          </FormLabel>    
          {/* <Input
            className="maintenance-form-field"
            placeholder="https://"
            name="url_image_2"
            data-type={`string`}
            type="url"
            onChange={handleChange}
            value={form[`url_image_2`] ? form[`url_image_2`] : ``}
          /> */}
        </FormControl>              
        <Upload 
          type={"live"}
          id={id}
          id2={"2"}
          style={{ marginTop: "10px"}}
          onChange={(files) => {
            setFiles_2(files);        
          }}      
          onNewFile={(newFiles) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onDeleteFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onUpdateFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }} 
        />
      </div>
    );
  };

  return (
    <MainLayout isLoading={isLoading} setIsLoading={setIsLoading}>
      <div id="live-form-content">
        <MaintenanceForm 
          primaryKey={"id"}
          primaryValue={id}
          primaryName={primaryName}
          config={{
            submitFields: [ 
              `start_time`,
              `end_time`,
              `title`,
              `subtitle`,
              `description`,
              `url_image_1`,
              `url_image_2`,
              `days_of_week`,
              `enabled`,
            ],
          }}
          menuConfig={{}}
          itemNameSingular={itemNameSingular}
          itemIsMale={true}
          actions={liveActions} 
          setIsLoading={setIsLoading}
          renderForm={renderForm}
          form={form}
          setForm={setForm}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          ref={childRef}
          isNew={isNew}
          page={"live"}
          executeAfterSaving={async (theId) => {
            return new Promise((resolve) => {
              if (!isDirtyFiles) {
                resolve({ success: true });
              }
              dispatch(filesActions.setFiles("live", theId, "1", files_1)).then(
                () => {
                  dispatch(filesActions.setFiles("live", theId, "2", files_2)).then(
                    () => {
                      resolve({ success: true });
                    },
                    (err) => resolve({ success: false, msg: err })
                  );
                },
                (err) => resolve({ success: false, msg: err })
              );
            });
          }}
        />
      </div>
    </MainLayout>
  );
};

export default LiveForm;
